import type { Board, Fen, Move, ParsedGame, PieceType, Position, San, Square } from '@chess/api-model';

/** A move as a board hands it over: where from, where to, and what a pawn becomes. */
export interface MoveInput {
  from: Square;
  to: Square;
  promotion?: PieceType;
}

/**
 * The rules of chess. Stateless on purpose: every call takes a position and
 * returns data, so nothing with a lifecycle crosses a plugin boundary and any
 * two plugins asking about the same position get the same answer.
 *
 * Provided under the name `chess`; see `CHESS_API`.
 */
export interface Chess {
  /** The standard starting position. */
  readonly startFen: Fen;

  /** Whether a FEN string describes a position this provider can play from. */
  validateFen(fen: Fen): boolean;

  /** What is true of a position. */
  position(fen: Fen): Position;

  /** The pieces on the board. */
  board(fen: Fen): Board;

  /** Every legal move from a position, or only those from one square. */
  legalMoves(fen: Fen, from?: Square): Move[];

  /**
   * Play one move. Returns the move as played and the position it led to,
   * or `undefined` if the move is not legal from that position.
   */
  play(fen: Fen, move: San | MoveInput): { move: Move; fen: Fen } | undefined;

  /** Read a game from PGN. Throws on text that is not a game. */
  parsePgn(pgn: string): ParsedGame;

  /** Write a game as PGN from its tags and the moves in order. */
  toPgn(headers: Record<string, string>, sans: San[]): string;
}

/** The name the chess service is registered under. */
export const CHESS_API = 'chess';

/** The version of this interface, for `provides` and `requires`. */
export const CHESS_API_VERSION = '1.0.0';
